import { Type, type Static } from '@sinclair/typebox'
import { Value } from '@sinclair/typebox/value'

export const PROTOCOL_ROUTE_METHODS = ['GET', 'HEAD', 'POST', 'PATCH', 'PUT', 'DELETE', 'OPTIONS'] as const
export const PROTOCOL_MUTATING_METHODS = ['POST', 'PATCH', 'PUT', 'DELETE'] as const
export const PROTOCOL_ROUTE_PATH_MATCHES = ['exact', 'prefix', 'pattern'] as const
export const PROTOCOL_ROUTE_STREAM_CLASSES = ['none', 'binary', 'sse', 'sse-optional', 'websocket', 'proxy'] as const
export const PROTOCOL_ROUTE_CACHE_BEHAVIORS = [
  'unspecified',
  'request-hash',
  'immutable',
  'conditional',
  'no-cache',
  'no-store',
] as const
export const PROTOCOL_ROUTE_DURABILITY_TAGS = [
  'none',
  'revisioned-command',
  'durable-generation',
  'server-job',
] as const
export const PROTOCOL_ROUTE_RESPONSE_CLASSES = [
  'structured',
  'binary',
  'sse',
  'structured-or-sse',
  'websocket',
  'proxy',
] as const

export const ProtocolRouteMethodSchema = Type.Union(PROTOCOL_ROUTE_METHODS.map((method) => Type.Literal(method)))
export const ProtocolRoutePathMatchSchema = Type.Union(PROTOCOL_ROUTE_PATH_MATCHES.map((match) => Type.Literal(match)))
export const ProtocolRouteStreamClassSchema = Type.Union(
  PROTOCOL_ROUTE_STREAM_CLASSES.map((stream) => Type.Literal(stream)),
)
export const ProtocolRouteCacheBehaviorSchema = Type.Union(
  PROTOCOL_ROUTE_CACHE_BEHAVIORS.map((cache) => Type.Literal(cache)),
)
export const ProtocolRouteDurabilityTagSchema = Type.Union(
  PROTOCOL_ROUTE_DURABILITY_TAGS.map((durability) => Type.Literal(durability)),
)
export const ProtocolRouteResponseClassSchema = Type.Union(
  PROTOCOL_ROUTE_RESPONSE_CLASSES.map((response) => Type.Literal(response)),
)

export const PROTOCOL_ROUTE_OPERATION_CATALOG = [
  {
    id: 'health',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/health',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'auth-status',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/auth/status',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'auth-setup',
    methods: ['POST'],
    path: '/api/v1/auth/setup',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'auth-login',
    methods: ['POST'],
    path: '/api/v1/auth/login',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'auth-crypto',
    methods: ['POST'],
    path: '/api/v1/auth/crypto',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'bootstrap',
    methods: ['GET'],
    path: '/api/v1/bootstrap',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'ownership',
    methods: ['GET'],
    path: '/api/v1/ownership',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-occupancy-snapshot',
    methods: ['GET'],
    path: '/api/v1/chat-occupancies',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-occupancy-claim',
    methods: ['POST'],
    path: '/api/v1/chat-occupancies/:chatId/claim',
    match: 'pattern',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-occupancy-renew',
    methods: ['PUT'],
    path: '/api/v1/chat-occupancies/:chatId/lease',
    match: 'pattern',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-occupancy-release',
    methods: ['DELETE'],
    path: '/api/v1/chat-occupancies/:chatId',
    match: 'pattern',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-occupancy-switch',
    methods: ['POST'],
    path: '/api/v1/chat-occupancies/switch',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-occupancy-normalize',
    methods: ['POST'],
    path: '/api/v1/chat-occupancies/normalize',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'diagnostics-read',
    methods: ['GET'],
    path: '/api/v1/diagnostics',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'support-diagnostics-read',
    methods: ['GET'],
    path: '/api/v1/support/diagnostics',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'support-diagnostics-state',
    methods: ['GET'],
    path: '/api/v1/support/diagnostics/state',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'browser-diagnostics-upload',
    methods: ['POST'],
    path: '/api/v1/diagnostics/browser',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'startup-telemetry',
    methods: ['POST'],
    path: '/api/v1/telemetry/startup',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'settings-read',
    methods: ['GET'],
    path: '/api/v1/settings',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'shell-resource-read',
    methods: ['GET'],
    path: '/api/v1/resources/shell',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'standalone-setting-resource-read',
    methods: ['GET'],
    path: '/api/v1/resources/settings/:setting',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'inlay-catalog-read',
    methods: ['GET'],
    path: '/api/v1/inlay-assets',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'settings-cache-read',
    methods: ['POST'],
    path: '/api/v1/settings',
    match: 'exact',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'settings-group-read',
    methods: ['GET'],
    path: '/api/v1/settings/:group',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'settings-group-cache-read',
    methods: ['POST'],
    path: '/api/v1/settings/:group',
    match: 'pattern',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'collections-read',
    methods: ['GET'],
    path: '/api/v1/collections',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'collections-cache-read',
    methods: ['POST'],
    path: '/api/v1/collections',
    match: 'exact',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'collection-read',
    methods: ['GET'],
    path: '/api/v1/collections/:name',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'collection-cache-read',
    methods: ['POST'],
    path: '/api/v1/collections/:name',
    match: 'pattern',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-aggregate-read',
    methods: ['GET'],
    path: '/api/v1/characters/aggregate',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-aggregate-cache-read',
    methods: ['POST'],
    path: '/api/v1/characters/aggregate',
    match: 'exact',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'characters-read',
    methods: ['GET'],
    path: '/api/v1/characters',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'characters-cache-read',
    methods: ['POST'],
    path: '/api/v1/characters',
    match: 'exact',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-order-read',
    methods: ['GET'],
    path: '/api/v1/characters/order',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-selection-read',
    methods: ['GET'],
    path: '/api/v1/characters/:id/selection',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-read',
    methods: ['GET'],
    path: '/api/v1/characters/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-greeting-translations-read',
    methods: ['GET'],
    path: '/api/v1/characters/:characterId/greeting-translations',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-messages-read',
    methods: ['GET'],
    path: '/api/v1/chats/:id/messages',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-messages-bulk-read',
    methods: ['POST'],
    path: '/api/v1/chats/messages/bulk',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-lore-token-counts',
    methods: ['GET'],
    path: '/api/v1/chats/:chatId/lore-token-counts',
    match: 'pattern',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'chat-display-sources',
    methods: ['POST'],
    path: '/api/v1/chats/:chatId/display-sources',
    match: 'pattern',
    streaming: 'sse-optional',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured-or-sse',
  },
  {
    id: 'character-lorebook-read',
    methods: ['GET'],
    path: '/api/v1/characters/:id/lorebook',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-lorebook-cache-read',
    methods: ['POST'],
    path: '/api/v1/characters/:id/lorebook',
    match: 'pattern',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'character-lorebooks-bulk-read',
    methods: ['POST'],
    path: '/api/v1/characters/lorebooks/bulk',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'legacy-preset-read',
    methods: ['GET'],
    path: '/api/v1/legacy-presets/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'legacy-preset-cache-read',
    methods: ['POST'],
    path: '/api/v1/legacy-presets/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'prompt-preset-template-read',
    methods: ['GET'],
    path: '/api/v1/prompt-presets/:id/template',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'prompt-preset-template-cache-read',
    methods: ['POST'],
    path: '/api/v1/prompt-presets/:id/template',
    match: 'pattern',
    streaming: 'none',
    cache: 'request-hash',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'risusave-import',
    methods: ['POST'],
    path: '/api/v1/import/risusave',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'risusave-bundle-import',
    methods: ['POST'],
    path: '/api/v1/import/bundle',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'risusave-export',
    methods: ['GET'],
    path: '/api/v1/export/risusave',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'risusave-bundle-export',
    methods: ['GET'],
    path: '/api/v1/export/bundle',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'risusave-local-backup-export',
    methods: ['GET'],
    path: '/api/v1/export/local-backup',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'realm-character-import',
    methods: ['POST'],
    path: '/api/v1/import/realm-character',
    match: 'exact',
    streaming: 'sse-optional',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured-or-sse',
  },
  {
    id: 'local-character-card-import',
    methods: ['POST'],
    path: '/api/v1/import/character-card',
    match: 'exact',
    streaming: 'sse-optional',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured-or-sse',
  },
  {
    id: 'local-module-import',
    methods: ['POST'],
    path: '/api/v1/import/module',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'command-mutation-receipt-ack',
    methods: ['POST'],
    path: '/api/v1/commands/mutation-receipts/ack',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'revisioned-command',
    response: 'structured',
  },
  {
    id: 'commands',
    methods: ['POST', 'PATCH', 'PUT', 'DELETE'],
    path: '/api/v1/commands/',
    match: 'prefix',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'revisioned-command',
    response: 'structured',
  },
  {
    id: 'bardwiki-vault-export',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/bardwiki/chats/:chatId/export',
    match: 'pattern',
    streaming: 'binary',
    cache: 'no-store',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'bardwiki-chat-read',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/bardwiki/chats/:chatId',
    match: 'pattern',
    streaming: 'none',
    cache: 'conditional',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'bardwiki-document-read',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/bardwiki/chats/:chatId/documents/:documentId',
    match: 'pattern',
    streaming: 'none',
    cache: 'conditional',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'bardwiki-document-versions-read',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/bardwiki/chats/:chatId/documents/:documentId/versions',
    match: 'pattern',
    streaming: 'none',
    cache: 'conditional',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'bardwiki-receipts-read',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/bardwiki/chats/:chatId/receipts',
    match: 'pattern',
    streaming: 'none',
    cache: 'conditional',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'bardwiki-job-retry',
    methods: ['POST'],
    path: '/api/v1/bardwiki/jobs/:jobId/retry',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'bardwiki-job-cancel',
    methods: ['DELETE'],
    path: '/api/v1/bardwiki/jobs/:jobId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'events',
    methods: ['GET'],
    path: '/api/v1/events',
    match: 'exact',
    streaming: 'sse',
    cache: 'no-cache',
    durability: 'none',
    response: 'sse',
  },
  {
    id: 'asset-upload',
    methods: ['POST'],
    path: '/api/v1/assets',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'asset-bulk-upload',
    methods: ['POST'],
    path: '/api/v1/assets/bulk',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'asset-read',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/assets/:id',
    match: 'pattern',
    streaming: 'binary',
    cache: 'immutable',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'asset-exists',
    methods: ['POST'],
    path: '/api/v1/assets/exists',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'backup-mutations',
    methods: ['POST', 'PATCH', 'PUT', 'DELETE'],
    path: '/api/v1/backups',
    match: 'prefix',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'storage-usage',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/storage-usage',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'backup-list',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/backups',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'request-history-list',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/request-history',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'request-history-detail',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/request-history/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'request-history-delete',
    methods: ['DELETE'],
    path: '/api/v1/request-history/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'push-vapid-public-key',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/push/vapid-public-key',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'push-subscription-create',
    methods: ['POST'],
    path: '/api/v1/push/subscriptions',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'push-subscription-delete',
    methods: ['DELETE'],
    path: '/api/v1/push/subscriptions',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'mcp-oauth-refresh',
    methods: ['POST'],
    path: '/api/v1/mcp/oauth/refresh',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'embedding-operations',
    methods: ['POST'],
    path: '/api/v1/embedding-operations',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'provider-operations',
    methods: ['POST'],
    path: '/api/v1/provider-operations',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'openai-transcription',
    methods: ['POST'],
    path: '/api/v1/media/openai/transcriptions',
    match: 'exact',
    streaming: 'none',
    cache: 'no-store',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'tts-synthesis',
    methods: ['POST'],
    path: '/api/v1/tts/synthesize',
    match: 'exact',
    streaming: 'binary',
    cache: 'no-store',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'image-generation',
    methods: ['POST'],
    path: '/api/v1/image-generation',
    match: 'exact',
    streaming: 'binary',
    cache: 'no-store',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'proxy-fetch',
    methods: ['GET', 'POST', 'PUT', 'DELETE'],
    path: '/api/v1/proxy/fetch',
    match: 'exact',
    streaming: 'proxy',
    cache: 'unspecified',
    durability: 'none',
    response: 'proxy',
  },
  {
    id: 'proxy-plugin-fetch',
    methods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'],
    path: '/api/v1/proxy/plugin-fetch',
    match: 'exact',
    streaming: 'proxy',
    cache: 'unspecified',
    durability: 'none',
    response: 'proxy',
  },
  {
    id: 'proxy-stream-job-create',
    methods: ['POST'],
    path: '/api/v1/proxy/stream-jobs',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'proxy-stream-job-cancel',
    methods: ['DELETE'],
    path: '/api/v1/proxy/stream-jobs/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'proxy-stream-job-websocket',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/proxy/stream-jobs/:id/ws',
    match: 'pattern',
    streaming: 'websocket',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'websocket',
  },
  {
    id: 'hub-proxy',
    methods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'],
    path: '/api/v1/hub/*',
    match: 'pattern',
    streaming: 'proxy',
    cache: 'unspecified',
    durability: 'none',
    response: 'proxy',
  },
  {
    id: 'legacy-storage-list',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/storage/list',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'legacy-storage-read',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/storage/read',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'legacy-storage-exists',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/storage/exists',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'legacy-storage-write',
    methods: ['POST'],
    path: '/api/v1/storage/write',
    match: 'exact',
    streaming: 'binary',
    cache: 'unspecified',
    durability: 'none',
    response: 'binary',
  },
  {
    id: 'legacy-storage-remove',
    methods: ['POST'],
    path: '/api/v1/storage/remove',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'generation-completion',
    methods: ['POST'],
    path: '/api/v1/generate/completion',
    match: 'exact',
    streaming: 'sse-optional',
    cache: 'no-store',
    durability: 'none',
    response: 'structured-or-sse',
  },
  {
    id: 'generation-operation-submit',
    methods: ['POST'],
    path: '/api/v1/generation-operations',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-operation-status',
    methods: ['GET'],
    path: '/api/v1/generation-operations/:operationId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-operation-stream',
    methods: ['GET'],
    path: '/api/v1/generation-operations/:operationId/stream',
    match: 'pattern',
    streaming: 'sse',
    cache: 'no-cache',
    durability: 'durable-generation',
    response: 'sse',
  },
  {
    id: 'generation-operation-cancel',
    methods: ['PUT'],
    path: '/api/v1/generation-operations/:operationId/cancellation',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-operation-retry',
    methods: ['POST'],
    path: '/api/v1/generation-operations/:operationId/retries',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-effect-status',
    methods: ['GET'],
    path: '/api/v1/generation-effects/:generationId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-effect-claim',
    methods: ['POST'],
    path: '/api/v1/generation-effects/:generationId/:effectKind/claims',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-effect-lease',
    methods: ['PUT'],
    path: '/api/v1/generation-effects/:generationId/:effectKind/lease',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-effect-receipt',
    methods: ['PUT'],
    path: '/api/v1/generation-effects/:generationId/:effectKind/receipt',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-effect-igp-commit',
    methods: ['PUT'],
    path: '/api/v1/generation-effects/:generationId/igp/commit',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'revisioned-command',
    response: 'structured',
  },
  {
    id: 'generation-effect-inlay-preparation',
    methods: ['PUT'],
    path: '/api/v1/generation-effects/:generationId/igp/inlay-preparation',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'revisioned-command',
    response: 'structured',
  },
  {
    id: 'generation-effect-inlay-finalization',
    methods: ['PUT'],
    path: '/api/v1/generation-effects/:generationId/igp/inlay-finalization',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'revisioned-command',
    response: 'structured',
  },
  {
    id: 'generation-effect-inlay-abandonment',
    methods: ['PUT'],
    path: '/api/v1/generation-effects/:generationId/igp/inlay-abandonment',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'revisioned-command',
    response: 'structured',
  },
  {
    id: 'generation-effect-igp-completion',
    methods: ['POST'],
    path: '/api/v1/generation-effects/:generationId/igp/completion',
    match: 'pattern',
    streaming: 'none',
    cache: 'no-store',
    durability: 'durable-generation',
    response: 'structured',
  },
  {
    id: 'generation-chat',
    methods: ['POST'],
    path: '/api/v1/generate/chat',
    match: 'exact',
    streaming: 'sse',
    cache: 'no-cache',
    durability: 'server-job',
    response: 'sse',
  },
  {
    id: 'generation-chat-reattach',
    methods: ['GET'],
    path: '/api/v1/generate/chat/:id/stream',
    match: 'pattern',
    streaming: 'sse',
    cache: 'no-cache',
    durability: 'server-job',
    response: 'sse',
  },
  {
    id: 'generation-chat-terminal-snapshot',
    methods: ['GET'],
    path: '/api/v1/generate/chat/:id/terminal-snapshot',
    match: 'pattern',
    streaming: 'binary',
    cache: 'no-store',
    durability: 'server-job',
    response: 'binary',
  },
  {
    id: 'generation-chat-cancel',
    methods: ['DELETE'],
    path: '/api/v1/generate/chat/:id',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'generation-preview-prompt',
    methods: ['POST'],
    path: '/api/v1/generate/preview-prompt',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'memory-job-create',
    methods: ['POST'],
    path: '/api/v1/memory/jobs',
    match: 'exact',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'memory-job-list',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/memory/jobs',
    match: 'exact',
    streaming: 'none',
    cache: 'conditional',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'memory-job-cancel',
    methods: ['DELETE'],
    path: '/api/v1/memory/jobs/',
    match: 'prefix',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'server-job',
    response: 'structured',
  },
  {
    id: 'memory-chunks',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/memory/chunks/:chatId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'memory-summaries',
    methods: ['GET', 'HEAD'],
    path: '/api/v1/memory/summaries/:chatId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'memory-summary-update',
    methods: ['PATCH'],
    path: '/api/v1/memory/summaries/:summaryId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
  {
    id: 'memory-summary-delete',
    methods: ['DELETE'],
    path: '/api/v1/memory/summaries/:summaryId',
    match: 'pattern',
    streaming: 'none',
    cache: 'unspecified',
    durability: 'none',
    response: 'structured',
  },
] as const

export const PROTOCOL_ROUTE_OPERATION_IDS = PROTOCOL_ROUTE_OPERATION_CATALOG.map(({ id }) => id)
export const ProtocolRouteOperationIdSchema = Type.Union(PROTOCOL_ROUTE_OPERATION_IDS.map((id) => Type.Literal(id)))
export const ProtocolRouteOperationDescriptorSchema = Type.Object(
  {
    id: ProtocolRouteOperationIdSchema,
    methods: Type.Readonly(Type.Array(ProtocolRouteMethodSchema, { minItems: 1, uniqueItems: true })),
    path: Type.String({ pattern: '^/api/v1/' }),
    match: ProtocolRoutePathMatchSchema,
    streaming: ProtocolRouteStreamClassSchema,
    cache: ProtocolRouteCacheBehaviorSchema,
    durability: ProtocolRouteDurabilityTagSchema,
    response: ProtocolRouteResponseClassSchema,
  },
  { additionalProperties: false },
)

export type ProtocolRouteMethod = Static<typeof ProtocolRouteMethodSchema>
export type ProtocolRoutePathMatch = Static<typeof ProtocolRoutePathMatchSchema>
export type ProtocolRouteStreamingShape = Static<typeof ProtocolRouteStreamClassSchema>
export type ProtocolRouteCacheBehavior = Static<typeof ProtocolRouteCacheBehaviorSchema>
export type ProtocolRouteDurabilityTag = Static<typeof ProtocolRouteDurabilityTagSchema>
export type ProtocolRouteResponseClass = Static<typeof ProtocolRouteResponseClassSchema>
export type ProtocolRouteOperationId = Static<typeof ProtocolRouteOperationIdSchema>
type ProtocolRouteOperationDescriptorValue = Static<typeof ProtocolRouteOperationDescriptorSchema>
export type ProtocolRouteOperationDescriptor = Omit<ProtocolRouteOperationDescriptorValue, 'methods'> & {
  readonly methods: readonly ProtocolRouteMethod[]
}

const operationById = new Map<ProtocolRouteOperationId, ProtocolRouteOperationDescriptor>(
  PROTOCOL_ROUTE_OPERATION_CATALOG.map((operation) => [operation.id, operation]),
)
const routePatternCache = new Map<string, RegExp>()

export function isProtocolRouteOperationDescriptor(value: unknown): value is ProtocolRouteOperationDescriptor {
  return Value.Check(ProtocolRouteOperationDescriptorSchema, value)
}

export function findProtocolRouteOperationById(
  id: ProtocolRouteOperationId,
): ProtocolRouteOperationDescriptor | undefined {
  return operationById.get(id)
}

export function isProtocolMutatingMethod(method: string): boolean {
  return (PROTOCOL_MUTATING_METHODS as readonly string[]).includes(method.toUpperCase())
}

export function protocolRouteOperationMatches(
  operation: ProtocolRouteOperationDescriptor,
  method: string,
  path: string,
): boolean {
  if (!(operation.methods as readonly string[]).includes(method.toUpperCase())) return false
  if (operation.match === 'exact') return path === operation.path
  if (operation.match === 'prefix') return path.startsWith(operation.path)
  return routePatternRegExp(operation.path).test(path)
}

function routePatternRegExp(pattern: string): RegExp {
  const cached = routePatternCache.get(pattern)
  if (cached) return cached

  let source = '^'
  for (let i = 0; i < pattern.length; ) {
    const ch = pattern[i]
    if (ch === ':') {
      i += 1
      while (i < pattern.length && /[A-Za-z0-9_]/.test(pattern[i] ?? '')) i += 1
      source += '[^/]+'
      continue
    }
    if (ch === '*') {
      source += '.*'
      i += 1
      continue
    }
    source += escapeRegExp(ch ?? '')
    i += 1
  }
  source += '$'

  const regexp = new RegExp(source)
  routePatternCache.set(pattern, regexp)
  return regexp
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
